import {
  Paper,
  Typography,
  useDeviceDetect,
  DropdownWithController,
  TextInputWithController,
} from '@amperio/amperio-ui';

import { useTranslation } from '@utils';
import {
  translationStrings,
  getRoofOrGroundDropdownOptions,
  getPaymentMethodDropdownOptions,
  getInstallationSiteDropdownOptions,
  getProductCategoriesDropdownOptions,
  getLevelOfInsulationDropdownOptions,
  getMonthOfTheGivenBillDropdownOptions,
  getCurrentTypeOfHeatingDropdownOptions,
} from './product-categories-form.defaults';

import { IProductCategoriesFormProps } from './product-categories-form.types';
import { EProductCategory } from '@enums';

import './product-categories-form.styles.scss';

export const ProductCategoriesForm = ({
  watch,
  control,
  isEditMode,
}: IProductCategoriesFormProps): JSX.Element => {
  const { deviceData } = useDeviceDetect();
  const translations = useTranslation(translationStrings);

  const selectedProductCategories = watch('productCategories.productCategories');

  const isFoilHeaters = selectedProductCategories.includes(EProductCategory.FOIL_HEATERS);
  const isHeatingPumps = selectedProductCategories.includes(EProductCategory.HEATING_PUMPS);
  const isPhotovoltaics = selectedProductCategories.includes(EProductCategory.PHOTOVOLTAICS);

  const roofOrGroundDropdownOptions = getRoofOrGroundDropdownOptions(translations);
  const paymentMethodDropdownOptions = getPaymentMethodDropdownOptions(translations);
  const installationSiteDropdownOptions = getInstallationSiteDropdownOptions(translations);
  const productCategoriesDropdownOptions = getProductCategoriesDropdownOptions(translations);
  const levelOfInsulationDropdownOptions = getLevelOfInsulationDropdownOptions(translations);
  const monthOfTheGivenBillDropdownOptions = getMonthOfTheGivenBillDropdownOptions(translations);
  const currentTypeOfHeatingDropdownOptions = getCurrentTypeOfHeatingDropdownOptions(translations);

  return (
    <Paper padding={deviceData.isMobile ? 'sm' : 'md'} className="product-categories-form-wrapper">
      <Typography as={deviceData.isMobile ? 'h3' : 'h2'} strong>
        {translations.formProductCategoriesHeader}
      </Typography>
      <div className="product-categories-form-wrapper__form">
        <DropdownWithController
          formControlProps={{
            disabled: !isEditMode,
          }}
          dropdownProps={{
            multiple: true,
            options: productCategoriesDropdownOptions,
            labelId: 'product-categories-dropdown-label',
            label: translations.formProductCategoriesLabel,
          }}
          controllerProps={{
            control,
            name: 'productCategories.productCategories',
            rules: {
              required: {
                value: true,
                message: translations.commonFormFieldErrorRequired,
              },
            },
          }}
        />
        {selectedProductCategories.length > 0 ? (
          <DropdownWithController
            formControlProps={{
              disabled: !isEditMode,
            }}
            dropdownProps={{
              options: paymentMethodDropdownOptions,
              labelId: 'payment-method-dropdown-label',
              label: translations.formProductCategoriesPaymentMethodLabel,
            }}
            controllerProps={{
              control,
              name: 'productCategories.paymentMethod',
              rules: {
                required: {
                  value: true,
                  message: translations.commonFormFieldErrorRequired,
                },
              },
            }}
          />
        ) : null}
        {isHeatingPumps || isFoilHeaters ? (
          <>
            <TextInputWithController
              controllerProps={{
                control,
                name: 'productCategories.houseArea',
                rules: {
                  min: {
                    value: 80,
                    message: translations.formProductCategoriesHouseAreaMinError,
                  },
                  max: {
                    value: 5000,
                    message: translations.formProductCategoriesHouseAreaMaxError,
                  },
                },
              }}
              inputProps={{
                type: 'number',
                disabled: !isEditMode,
                label: translations.formProductCategoriesHouseAreaLabel,
              }}
            />
            <DropdownWithController
              formControlProps={{
                disabled: !isEditMode,
              }}
              dropdownProps={{
                options: levelOfInsulationDropdownOptions,
                labelId: 'level-of-insulation-dropdown-label',
                label: translations.formProductCategoriesLevelOfInsulationLabel,
              }}
              controllerProps={{
                control,
                name: 'productCategories.levelOfInsulation',
                rules: {
                  required: {
                    value: true,
                    message: translations.commonFormFieldErrorRequired,
                  },
                },
              }}
            />
            <DropdownWithController
              formControlProps={{
                disabled: !isEditMode,
              }}
              dropdownProps={{
                options: currentTypeOfHeatingDropdownOptions,
                labelId: 'current-type-of-heating-dropdown-label',
                label: translations.formProductCategoriesCurrentTypeOfHeatingLabel,
              }}
              controllerProps={{
                control,
                name: 'productCategories.currentTypeOfHeating',
                rules: {
                  required: {
                    value: true,
                    message: translations.commonFormFieldErrorRequired,
                  },
                },
              }}
            />
          </>
        ) : null}
        {isFoilHeaters ? (
          <DropdownWithController
            formControlProps={{
              disabled: !isEditMode,
            }}
            dropdownProps={{
              multiple: true,
              options: installationSiteDropdownOptions,
              labelId: 'installation-site-dropdown-label',
              label: translations.formProductCategoriesInstallationSiteLabel,
            }}
            controllerProps={{
              control,
              name: 'productCategories.installationSite',
              rules: {
                required: {
                  value: true,
                  message: translations.commonFormFieldErrorRequired,
                },
              },
            }}
          />
        ) : null}
        {isPhotovoltaics ? (
          <>
            <DropdownWithController
              formControlProps={{
                disabled: !isEditMode,
              }}
              dropdownProps={{
                options: roofOrGroundDropdownOptions,
                labelId: 'roof-or-ground-dropdown-label',
                label: translations.formProductCategoriesRoofOrGroundLabel,
              }}
              controllerProps={{
                control,
                name: 'productCategories.roofOrGround',
                rules: {
                  required: {
                    value: true,
                    message: translations.commonFormFieldErrorRequired,
                  },
                },
              }}
            />
            <TextInputWithController
              controllerProps={{
                control,
                name: 'productCategories.roofArea',
                rules: {
                  min: {
                    value: 20,
                    message: translations.formProductCategoriesRoofAreaMinError,
                  },
                  max: {
                    value: 1000,
                    message: translations.formProductCategoriesRoofAreaMaxError,
                  },
                },
              }}
              inputProps={{
                type: 'number',
                disabled: !isEditMode,
                label: translations.formProductCategoriesRoofAreaLabel,
              }}
            />
            <TextInputWithController
              controllerProps={{
                control,
                name: 'productCategories.monthlyElectricBill',
                rules: {
                  required: {
                    value: true,
                    message: translations.commonFormFieldErrorRequired,
                  },
                  min: {
                    value: 20,
                    message: translations.formProductCategoriesMonthlyElectricBillMinError,
                  },
                  max: {
                    value: 1000,
                    message: translations.formProductCategoriesMonthlyElectricBillMaxError,
                  },
                },
              }}
              inputProps={{
                type: 'number',
                disabled: !isEditMode,
                label: translations.formProductCategoriesMonthlyElectricBillLabel,
              }}
            />
            <DropdownWithController
              formControlProps={{
                disabled: !isEditMode,
              }}
              dropdownProps={{
                options: monthOfTheGivenBillDropdownOptions,
                labelId: 'month-of-the-given-bill-dropdown-label',
                label: translations.formProductCategoriesMonthOfTheGivenBillLabel,
              }}
              controllerProps={{
                control,
                name: 'productCategories.monthOfTheGivenBill',
                rules: {
                  required: {
                    value: true,
                    message: translations.commonFormFieldErrorRequired,
                  },
                },
              }}
            />
          </>
        ) : null}
      </div>
    </Paper>
  );
};
